import { useRef } from "react"

type UsePinnedResultsInput<T> = {
  results: T[]
  isPinned(item: T): boolean
}

/**
 * Caches items from `results` that match `isPinned` as they appear,
 * then returns the cached items that are no longer in the current results.
 * Useful for keeping already-added items visible when search changes.
 */
export function usePinnedResults<T extends { Id: number }>(
  input: UsePinnedResultsInput<T>,
) {
  const { results, isPinned } = input
  const cacheRef = useRef(new Map<number, T>())

  const resultIds = new Set<number>()
  for (const item of results) {
    resultIds.add(item.Id)
    if (isPinned(item)) {
      cacheRef.current.set(item.Id, item)
    }
  }

  if (cacheRef.current.size === 0) return []

  const pinned: T[] = []
  for (const item of cacheRef.current.values()) {
    if (!resultIds.has(item.Id)) {
      pinned.push(item)
    }
  }
  return pinned
}
